import React from 'react';
import Icon from '../../../components/AppIcon';

/**
 * InterruptionInsights — displays barge-in and filler word analysis.
 *
 * Now uses server-computed metrics (computedMetrics) as the source of truth.
 * Falls back to local analysis ONLY if computedMetrics is not available.
 * "right", "well", "actually" are excluded from fillers to prevent false positives.
 */

// Reduced filler list — excludes false-positive-prone words
const FILLER_WORDS = [
  'um', 'umm', 'uh', 'uhh', 'basically', 'you know',
  'sort of', 'kind of', 'literally',
  'honestly', 'so yeah', 'and stuff', 'i mean'
];

const localAnalyze = (transcript) => {
  const safeTranscript = transcript ?? [];
  let bargeIns = 0;
  let totalFillerCount = 0;
  let totalUserWords = 0;
  const fillerHits = {};
  let userTurns = 0;

  for (let i = 0; i < safeTranscript.length; i++) {
    const entry = safeTranscript[i];
    const isAI = entry.speaker === 'AI' || entry.speaker === 'Interviewer';

    if (isAI && entry.text && entry.text.trim().endsWith('...')) {
      bargeIns++;
    }

    const isUser = entry.speaker === 'You' || entry.speaker === 'User' || entry.speaker === 'user';
    if (isUser && entry.text) {
      const lowerText = entry.text.toLowerCase();
      totalUserWords += lowerText.split(/\s+/).length;
      userTurns++;

      for (const filler of FILLER_WORDS) {
        const regex = new RegExp(`\\b${filler}\\b`, 'gi');
        const matches = lowerText.match(regex);
        if (matches) {
          totalFillerCount += matches.length;
          fillerHits[filler] = (fillerHits[filler] || 0) + matches.length;
        }
      }
    }
  }

  const topFillers = Object.entries(fillerHits)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const avgWordsPerTurn = userTurns > 0 ? Math.round(totalUserWords / userTurns) : 0;
  const fillerRate = totalUserWords > 0
    ? Math.round((totalFillerCount / totalUserWords) * 100)
    : 0;

  return { bargeIns, totalFillerCount, topFillers, fillerRate, avgWordsPerTurn, userTurns, totalUserWords };
};

const InterruptionInsights = ({ transcript, computedMetrics }) => {
  // Early exit if no transcript data at all
  if (!transcript || transcript.length === 0) return null;

  // Use server metrics if available, else fall back to local
  const localData = (!computedMetrics || !computedMetrics.totalUserWords) ? localAnalyze(transcript) : null;

  const bargeIns = computedMetrics?.interruptionCount ?? localData?.bargeIns ?? 0;
  const totalFillerCount = computedMetrics?.fillerWordCount ?? localData?.totalFillerCount ?? 0;
  const fillerRate = computedMetrics
    ? Math.round((computedMetrics.fillerWordRate || 0) * 100)
    : localData?.fillerRate ?? 0;
  const avgWordsPerTurn = computedMetrics?.avgWordsPerUserTurn ?? localData?.avgWordsPerTurn ?? 0;

  // For top fillers breakdown, we still need local analysis (server doesn't break down by word)
  const topFillers = localData?.topFillers ?? [];

  const getBargeInRating = () => {
    if (bargeIns === 0) return { label: 'Excellent', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' };
    if (bargeIns <= 2) return { label: 'Good', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (bargeIns <= 4) return { label: 'Fair', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-100' };
    return { label: 'Needs Work', color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-100' };
  };

  const getFillerRating = () => {
    if (fillerRate <= 2) return { label: 'Excellent', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-100' };
    if (fillerRate <= 5) return { label: 'Good', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-100' };
    if (fillerRate <= 10) return { label: 'Fair', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-100' };
    return { label: 'Needs Work', color: 'text-rose-500', bg: 'bg-rose-50', border: 'border-rose-100' };
  };

  const bargeInRating = getBargeInRating();
  const fillerRating = getFillerRating();


  return (
    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">

      {/* Barge-In / Interruption Card */}
      <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-slate-900 text-white shadow-sm">
            <Icon name="Zap" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Interruption Behaviour</h3>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5">Did you let the interviewer finish?</p>
          </div>
        </div>

        <div className="flex items-center gap-5 mb-6">
          <div className={`w-20 h-20 rounded-2xl ${bargeInRating.bg} border ${bargeInRating.border} flex flex-col items-center justify-center shrink-0`}>
            <span className={`text-3xl font-black ${bargeInRating.color}`}>{bargeIns}</span>
            <span className="text-[8px] font-black text-slate-400tracking-widest mt-0.5">Interrupts</span>
          </div>
          <div className="flex-1 space-y-2">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${bargeInRating.bg} border ${bargeInRating.border}`}>
              <Icon name={bargeIns === 0 ? "CheckCircle" : bargeIns <= 2 ? "Info" : "AlertTriangle"} size={12} className={bargeInRating.color} />
              <span className={`text-[10px] font-blacktracking-wider ${bargeInRating.color}`}>{bargeInRating.label}</span>
            </div>
            <p className="text-[11px] text-slate-600 font-medium leading-snug">
              {bargeIns === 0
                ? "Perfect — you waited for the interviewer to finish every time."
                : bargeIns <= 2
                ? `You interrupted ${bargeIns} time${bargeIns > 1 ? 's' : ''}. Minor — try to wait for natural pauses.`
                : `You interrupted ${bargeIns} times. Focus on active listening and patience.`}
            </p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
          <p className="text-[10px] font-black text-slate-400tracking-widest mb-1">Pro Tip</p>
          <p className="text-[11px] text-slate-600 font-medium">
            Wait for 1–2 seconds of silence before responding. Top candidates score higher on active listening.
          </p>
        </div>
      </div>

      {/* Filler Words Card */}
      <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-slate-900 text-white shadow-sm">
            <Icon name="Mic" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-black text-slate-900tracking-[0.15em]">Filler Word Usage</h3>
            <p className="text-[10px] text-slate-400 font-bold mt-0.5">Verbal habits that affect credibility</p>
          </div>
        </div>

        <div className="flex items-center gap-5 mb-5">
          <div className={`w-20 h-20 rounded-2xl ${fillerRating.bg} border ${fillerRating.border} flex flex-col items-center justify-center shrink-0`}>
            <span className={`text-3xl font-black ${fillerRating.color}`}>{totalFillerCount}</span>
            <span className="text-[8px] font-black text-slate-400tracking-widest mt-0.5">Fillers</span>
          </div>
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-3 text-[11px] font-bold text-slate-600">
              <span>{fillerRate}% of words</span>
              <span className="text-slate-300">•</span>
              <span>~{avgWordsPerTurn} words/turn</span>
            </div>
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${fillerRating.bg} border ${fillerRating.border}`}>
              <Icon name={fillerRate <= 5 ? "CheckCircle" : "AlertTriangle"} size={12} className={fillerRating.color} />
              <span className={`text-[10px] font-blacktracking-wider ${fillerRating.color}`}>{fillerRating.label}</span>
            </div>
          </div>
        </div>

        {topFillers.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[10px] font-black text-slate-400tracking-widest">Most Used</p>
            <div className="flex flex-wrap gap-2">
              {topFillers.map(([word, count]) => (
                <div
                  key={word}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg"
                >
                  <span className="text-[11px] font-black text-slate-700 capitalize">"{word}"</span>
                  <span className="text-[9px] font-black text-slate-400">×{count}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
            <p className="text-[11px] font-black text-emerald-600">🎉 No significant filler words detected!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterruptionInsights;
